import { useCallback, useEffect, useMemo, useState } from 'react';
import { GAME_CONFIG } from '../constants/config';
import { loadGameConfig as loadGameConfigFromService } from '../services/dataService';
import type { GameConfig } from '../types';
import useErrorHandler from './useErrorHandler';

interface UseGameConfigResult {
  gameConfig: GameConfig | null;
  loadGameConfig: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const STORAGE_KEY = 'gameConfig';

/**
 * Hook for managing game configuration
 */
export const useGameConfig = (): UseGameConfigResult => {
  const [gameConfig, setGameConfigState] = useState<GameConfig | null>(null);
  // Start with isLoading: true since loading begins immediately on mount
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const throwError = useErrorHandler();

  // Load game configuration (data service first, then localStorage fallback)
  const loadGameConfigHook = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try game-kit service first
      const remoteConfig = await loadGameConfigFromService();

      if (remoteConfig) {
        // Ensure config has all expected default values
        const completeConfig: GameConfig = {
          ...GAME_CONFIG,
          ...remoteConfig,
          firstCaseId: remoteConfig.firstCaseId || GAME_CONFIG.firstCaseId,
        };

        setGameConfigState(completeConfig);
        // Also update localStorage as backup
        localStorage.setItem(STORAGE_KEY, JSON.stringify(completeConfig));
        setIsLoading(false);
        return;
      }

      // Fallback to default values
      setGameConfigState({ ...GAME_CONFIG });
    } catch (error) {
      setError('Failed to load configuration');

      // Try localStorage as final fallback
      try {
        const localConfig = localStorage.getItem(STORAGE_KEY);
        if (localConfig) {
          const parsedConfig = JSON.parse(localConfig) as GameConfig;
          setGameConfigState(parsedConfig);
          return; // localStorage fallback succeeded, don't throw
        }
      } catch (_localError) {
        // localStorage also failed — fall through to throwError
      }

      throwError(
        error instanceof Error
          ? error
          : new Error('Failed to load configuration.'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [throwError]);

  // Merge remote config on top of defaults — new fields flow through automatically
  const effectiveConfig = useMemo(
    () => ({ ...GAME_CONFIG, ...(gameConfig ?? {}) }),
    [gameConfig],
  );

  // Load on mount
  useEffect(() => {
    loadGameConfigHook();
  }, [loadGameConfigHook]);

  return {
    gameConfig: effectiveConfig,
    loadGameConfig: loadGameConfigHook,
    isLoading,
    error,
  };
};
